import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';

import { MergeSessionsDto, StockTakeSession } from './stock-take.models';

@Injectable({
  providedIn: 'root',
})
export class StockTakeService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {
    console.log('🌐 Environment:', environment);
    console.log('🔗 StockTakeService baseUrl:', this.baseUrl);
  }

  // ---------------------------------------------------------------------------
  // 📦 UPLOAD FULL & PARTIAL DRAFTS
  // ---------------------------------------------------------------------------

  /**
   * Upload Excel file to create a FULL stock-take draft (no inventory change)
   */
  uploadFullStockTakeDraft(
    storeId: string,
    file: File,
    options?: { q?: string; limit?: number; page?: number },
  ) {
    let params = new HttpParams();
    if (options?.q) params = params.set('q', options.q);
    if (options?.limit) params = params.set('limit', options.limit);
    if (options?.page) params = params.set('page', options.page);

    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(
      `${this.baseUrl}/stock-take-draft/upload/full/${storeId}`,
      formData,
      {
        params,
      },
    );
  }

  /**
   * Upload Excel file to create a PARTIAL stock-take draft (only selected SKUs)
   */
  uploadPartialStockTakeDraft(
    storeId: string,
    file: File,
    options?: { q?: string; limit?: number; page?: number },
  ) {
    let params = new HttpParams();
    if (options?.q) params = params.set('q', options.q);
    if (options?.limit) params = params.set('limit', options.limit);
    if (options?.page) params = params.set('page', options.page);

    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(
      `${this.baseUrl}/stock-take-draft/upload/partial/${storeId}`,
      formData,
      {
        params,
      },
    );
  }

  // ---------------------------------------------------------------------------
  // 📋 DRAFT LISTING
  // ---------------------------------------------------------------------------

  /**
   * Get paginated list of stock-take drafts (for logged-in user)
   */
  getStockTakeDrafts(options?: {
    q?: string;
    status?: string;
    limit?: number;
    page?: number;
  }) {
    let params = new HttpParams();
    if (options?.q) params = params.set('q', options.q);
    if (options?.status) params = params.set('status', options.status);
    if (options?.limit) params = params.set('limit', options.limit);
    if (options?.page) params = params.set('page', options.page);

    return this.http.get(`${this.baseUrl}/stock-take-draft`, { params });
  }

  // ---------------------------------------------------------------------------
  // 📦 DRAFT ITEMS MANAGEMENT
  // ---------------------------------------------------------------------------

  /**
   * Get paginated list of items for a specific draft
   */
  getDraftItems(
    draftId: string,
    options?: { q?: string; limit?: number; page?: number },
  ) {
    let params = new HttpParams();
    if (options?.q) params = params.set('q', options.q);
    if (options?.limit) params = params.set('limit', options.limit);
    if (options?.page) params = params.set('page', options.page);

    return this.http.get(`${this.baseUrl}/stock-take-draft/${draftId}/items`, {
      params,
    });
  }

  /**
   * Add a variant/SKU to a draft
   */
  addDraftItem(
    draftId: string,
    body: { sku: string; variantId: string; newQty: number },
  ) {
    return this.http.post(
      `${this.baseUrl}/stock-take-draft/${draftId}/items`,
      body,
    );
  }

  /**
   * Update the quantity of an item in a draft
   */
  updateDraftItemQty(
    draftId: string,
    itemId: string,
    body: { newQty: number },
  ) {
    return this.http.patch(
      `${this.baseUrl}/stock-take-draft/${draftId}/item/${itemId}`,
      body,
    );
  }

  /**
   * Remove an item from a draft
   */
  deleteDraftItem(draftId: string, itemId: string) {
    return this.http.delete(
      `${this.baseUrl}/stock-take-draft/${draftId}/item/${itemId}`,
    );
  }

  // ---------------------------------------------------------------------------
  // 📝 DRAFT SAVE, SUBMIT, CLEANUP
  // ---------------------------------------------------------------------------

  /**
   * Save draft notes or keep as DRAFT
   */
  saveDraft(draftId: string): Observable<any> {
    // FIX: Pass an empty object ({}) as the body (the second argument)
    return this.http.patch(
      `${this.baseUrl}/stock-take-draft/${draftId}/save`,
      {},
    );
  }
  /**
   * Submit draft → apply to Inventory + Audit + Ledger
   */
  submitDraft(draftId: string) {
    return this.http.post(
      `${this.baseUrl}/stock-take-draft/${draftId}/submit`,
      {},
    );
  }

  /**
   * Cleanup old drafts (admin/cron)
   */
  cleanupDrafts() {
    return this.http.post(`${this.baseUrl}/stock-take-draft/cleanup`, {});
  }

  getAllStockTakes(params: any): Observable<any> {
    let query = new HttpParams();
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== '') {
        query = query.set(key, params[key]);
      }
    });
    return this.http.get(this.baseUrl, { params: query });
  }

  downloadStockTakePDF(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${id}/pdf`, { responseType: 'blob' });
  }

  deleteStockTake(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${id}`);
  }

  updateStockTakeItem(draftId: string, itemId: string, body: any) {
    return this.http.put(`/stock-take-draft/${draftId}/item/${itemId}`, body);
  }

  getDraftItemsPaginated(
    draftId: string,
    page: number,
    limit: number,
    search: string,
  ): Observable<any> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) {
      params = params.set('q', search);
    }

    return this.http.get(`${this.baseUrl}/stock-take-draft/${draftId}/items`, {
      params,
    });
  }

  downloadTemplate(): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/stock-take/template`, {
      responseType: 'blob',
    });
  }

  // ---------------------------------------------------------------------------
  // 📦 SESSION MANAGEMENT
  // ---------------------------------------------------------------------------

  /**
   * Get paginated list of sessions
   */
  getSessions(options?: {
    storeId?: string;
    fromDate?: string;
    toDate?: string;
    status?: string;
    sessionType?: string;
    limit?: number;
    page?: number;
  }) {
    let params = new HttpParams();
    if (options?.storeId) params = params.set('storeId', options.storeId);
    if (options?.fromDate) params = params.set('fromDate', options.fromDate);
    if (options?.toDate) params = params.set('toDate', options.toDate);
    if (options?.status) params = params.set('status', options.status);
    if (options?.sessionType)
      params = params.set('sessionType', options.sessionType);
    if (options?.limit) params = params.set('limit', options.limit);
    if (options?.page) params = params.set('page', options.page);

    return this.http.get<{ data: StockTakeSession[]; total: number }>(
      `${this.baseUrl}/stock-take/session`,
      { params },
    );
  }

  /**
   * Get session details. Pass `entries` options to page/search the entry list
   * server-side (sessions can hold thousands of lines); omit them to receive
   * every entry (legacy behaviour, used by mobile resume).
   */
  getSessionById(
    id: string,
    entries?: { page?: number; limit?: number; search?: string },
  ) {
    let params = new HttpParams();
    if (entries?.page) params = params.set('entriesPage', entries.page);
    if (entries?.limit) params = params.set('entriesLimit', entries.limit);
    if (entries?.search) params = params.set('entriesSearch', entries.search);
    return this.http.get<StockTakeSession & { entriesTotal?: number }>(
      `${this.baseUrl}/stock-take/session/${id}`,
      { params },
    );
  }

  /**
   * Merge sessions
   */
  mergeSessions(data: MergeSessionsDto) {
    return this.http.post<StockTakeSession>(
      `${this.baseUrl}/stock-take/session/merge`,
      data,
    );
  }

  /**
   * Complete session
   */
  completeSession(id: string, stockTakeType: string = 'FULL') {
    return this.http.post(`${this.baseUrl}/stock-take/session/complete/${id}`, {
      stockTakeType,
    });
  }

  /**
   * Compare session counts with System SOH
   */
  getComparison(id: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.baseUrl}/stock-take/session/${id}/comparison`,
    );
  }

  /**
   * Trace stock take counts back to individual sessions and users
   */
  getTrace(id: string, sku: string, shelf?: string): Observable<any[]> {
    let params = new HttpParams().set('sku', sku);
    if (shelf) {
      params = params.set('shelf', shelf);
    }
    return this.http.get<any[]>(
      `${this.baseUrl}/stock-take/session/${id}/trace`,
      {
        params,
      },
    );
  }

  /**
   * Upsert one entry of a session (used to correct a counted qty from the web).
   * Backend upserts on (sessionId, sku, shelf, rack).
   */
  upsertSessionEntry(data: {
    sessionId: string;
    sku: string;
    shelf: string;
    rack?: string;
    quantity: number;
    description?: string;
    variantPricingId?: string;
    deviceId?: string;
    scannedBy?: string;
  }) {
    return this.http.post<any>(
      `${this.baseUrl}/stock-take/session/entry`,
      data,
    );
  }

  downloadVarianceReport(id: string): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/stock-take/session/${id}/variance-report`,
      {
        responseType: 'blob',
      },
    );
  }

  downloadRegularReport(id: string): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/stock-take/session/${id}/regular-report`,
      {
        responseType: 'blob',
      },
    );
  }

  /**
   * Variance report for an Excel-upload draft. Same workbook format as the
   * session report above, just sourced from the draft lines.
   */
  downloadDraftVarianceReport(draftId: string): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/stock-take-draft/${draftId}/variance-report`,
      {
        responseType: 'blob',
      },
    );
  }
}
